import {useEffect, useMemo, useState} from "react";
import _ from "lodash";
import {useParams} from "react-router-dom";
import {useAtomValue} from "jotai";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";
import {Box, Stack, Tab, Tabs, Typography} from "@mui/material";
import {MainViewLayout} from "../MainViewLayout";
import {FieldSoilMoistureCard} from "./FieldSoilMoistureCard";
import {FieldHeader} from "./FieldHeader";
import {FieldIrrigationInsightsView} from "./FieldIrrigationInsightsView";
import {MenuItem} from "../../types/menuItem";
import {FieldVegetationInsightsView} from "./FieldVegetationInsightsView";
import {FieldWeatherInsightsView} from "./FieldWeatherInsightsView";
import {FieldOtherInsightsView} from "./FieldOtherInsightsView";
import {FieldCropGrowthCard} from "./FieldCropGrowthCard";
import {FieldWeatherCard} from "./FieldWeatherCard";
import {activeFieldResultsLoadable} from "../../store/activeFieldResultsStore";
import {HistoryDaysSelector} from "./HistoryDaysSelector";
import {activeCompanyResultsLoadable} from "../../store/activeCompanyResultsStore";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {useLoadableValueOrDefault} from "../../hooks/jotaiHooks";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {FieldResult} from "../../types/domain/fieldResult";
import {activeDateLoadable} from "../../store/activeDateStore";
import {aggregatedPropsToWeatherProps, normalizeCropGrowth, normalizeSoilMoisture} from "../../utils/insightsUtils";
import {InsightsIcon} from "../icons/InsightsIcon";
import {isMaderaCompany} from "../../utils/maderaUtils";
import {ParcelHeader} from "./ParcelHeader";
import {MaderaEtawCumulativeChart} from "./charts/MaderaEtawCumulativeChart";
import {useActiveField} from "../../store/activeFieldStore";
import {isMagdalenaCompany} from "../../utils/magdalenaUtils";
import {useCompany} from "../../hooks/companiesHooks";
import analytics from "../../services/analyticsService";
import {swallowException} from "../../utils/appUtils";

export const FieldInsightsView: React.FC = () => {
    const {t} = useTranslation();
    const [selectedTab, setSelectedTab] = useState<string>("irrigation");
    const {fieldId} = useParams();
    const activeCompanyResultsState = useAtomValue(activeCompanyResultsLoadable);
    const fieldStatusProps = useMemo(
        () =>
            activeCompanyResultsState.state === "hasData" && activeCompanyResultsState.data && fieldId
                ? activeCompanyResultsState.data[fieldId]
                : undefined,
        [activeCompanyResultsState, fieldId],
    );
    const company = useCompany();
    const isMadera = company?.data && isMaderaCompany(company?.data);
    const data = useLoadableValueOrDefault<FieldResult[]>(activeFieldResultsLoadable, []);
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const date = useLoadableValueOrDefault<dayjs.Dayjs | null>(activeDateLoadable, null);
    const weatherProps = useMemo(() => {
        if (
            data.length > 0 &&
            date?.isSame(data[data.length - 1].date) &&
            !_.isNil(data[data.length - 1].aggregatedProps.air_temperature_24_T1)
        ) {
            return aggregatedPropsToWeatherProps(data[data.length - 1].aggregatedProps);
        } else {
            return undefined;
        }
    }, [data, date]);
    const field = useActiveField();

    useEffect(() => {
        if (activeCompanyResultsState.state !== "hasData" || !fieldStatusProps) {
            return;
        }

        analytics
            .track("insights-section-view-select", {
                section: selectedTab,
            })
            .catch(swallowException);
    }, [activeCompanyResultsState.state, fieldStatusProps, selectedTab]);

    // TODO: Use a feature flag instead of hardcoded magdalena company id
    const canAccessOtherTab = () => {
        if (company?.data && isMagdalenaCompany(company?.data)) return true;

        return access.canAccessDataQualityAnalysis;
    };

    return (
        <>
            {isMadera && (
                <MainViewLayout header={<ParcelHeader />}>
                    <MaderaEtawCumulativeChart />
                </MainViewLayout>
            )}
            <MainViewLayout header={<FieldHeader />}>
                {activeCompanyResultsState.state === "hasData" && !fieldStatusProps && (
                    <Box padding="1.5rem" bgcolor="#FAFCFF" width="100%" border={1} borderColor="#E9EBF1 !important">
                        {t("insights.field_insights_view.no_results")}
                    </Box>
                )}
                {activeCompanyResultsState.state === "hasData" && fieldStatusProps && (
                    <Stack direction="column" gap="1.5rem">
                        <Stack direction="row" width="100%" gap="1.2rem">
                            <Box
                                padding="1.5rem"
                                bgcolor="#FAFCFF"
                                width="100%"
                                border={1}
                                borderColor="#E9EBF1 !important"
                            >
                                <FieldSoilMoistureCard
                                    soilMoisturePercentage={normalizeSoilMoisture(
                                        fieldStatusProps?.soilMoisture,
                                        false,
                                    )}
                                    isLoading={false}
                                    threshold={field?.irrigationCriteria ?? undefined}
                                    vegetationCover={fieldStatusProps?.vegetationCover}
                                />
                            </Box>
                            <Box
                                padding="1.5rem"
                                bgcolor="#FAFCFF"
                                width="100%"
                                border={1}
                                borderColor="#E9EBF1 !important"
                            >
                                <FieldCropGrowthCard
                                    cropGrowthPercentage={normalizeCropGrowth(fieldStatusProps?.cropGrowth)}
                                    vegetationCover={fieldStatusProps?.vegetationCover}
                                    isLoading={false}
                                />
                            </Box>
                            <Box
                                padding="1.5rem"
                                bgcolor="#FAFCFF"
                                width="100%"
                                border={1}
                                borderColor="#E9EBF1 !important"
                            >
                                <FieldWeatherCard weatherProps={weatherProps} isLoading={false} />
                            </Box>
                        </Stack>
                        <Stack direction="column">
                            <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                                <Stack direction="row" alignItems="center" gap="0.5rem">
                                    <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                                        {t("insights.field_insights_view.insights")}
                                    </Typography>
                                    <Tabs
                                        value={selectedTab}
                                        orientation="horizontal"
                                        TabIndicatorProps={{
                                            sx: {
                                                height: "3px",
                                            },
                                        }}
                                        sx={{
                                            minHeight: "auto",
                                            color: "#000000A0",
                                            "& a:hover": {backgroundColor: "#eceff5"},
                                            "& .Mui-selected": {color: "#4855bf"},
                                        }}
                                    >
                                        {getTabItems(canAccessOtherTab()).map((item) => (
                                            <Tab
                                                color="primary"
                                                key={item.href}
                                                label={t(item.i18nLabel)}
                                                value={item.href}
                                                id={item.href}
                                                onClick={() => setSelectedTab(item.href)}
                                                sx={{
                                                    minHeight: "2rem",
                                                    minWidth: 0,
                                                    height: "2rem",
                                                    padding: 0,
                                                    marginX: "0.5rem",
                                                    fontWeight: 500,
                                                    fontSize: 16,
                                                }}
                                            />
                                        ))}
                                    </Tabs>
                                </Stack>
                                <Stack direction="row" alignItems="center" gap="0.5rem">
                                    <InsightsIcon
                                        sx={{
                                            stroke: "#404DB8",
                                            width: "1.125rem",
                                            height: "1.125rem",
                                        }}
                                    />
                                    <Box>
                                        <Typography textTransform="uppercase" fontSize={14} noWrap>
                                            {t("insights.field_insights_view.show_history")}
                                        </Typography>
                                    </Box>
                                    <HistoryDaysSelector />
                                </Stack>
                            </Stack>
                            <Box padding="1.5rem" bgcolor="#FAFCFF">
                                {selectedTab === "irrigation" && <FieldIrrigationInsightsView data={data} />}
                                {selectedTab === "vegetation" && <FieldVegetationInsightsView data={data} />}
                                {selectedTab === "weather" && <FieldWeatherInsightsView data={data} />}
                                {canAccessOtherTab() && selectedTab === "other" && (
                                    <FieldOtherInsightsView data={data} />
                                )}
                            </Box>
                        </Stack>
                    </Stack>
                )}
            </MainViewLayout>
        </>
    );
};

const getTabItems = (enableOtherTab: boolean): MenuItem[] => [
    {
        i18nLabel: "insights.field_insights_view.menu.irrigation",
        href: "irrigation",
    },
    {
        i18nLabel: "insights.field_insights_view.menu.vegetation",
        href: "vegetation",
    },
    {
        i18nLabel: "insights.field_insights_view.menu.weather",
        href: "weather",
    },
    ...(enableOtherTab
        ? [
              {
                  i18nLabel: "insights.field_insights_view.menu.other",
                  href: "other",
              },
          ]
        : []),
];

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canAccessDataQualityAnalysis"]);
